import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { alertsApi } from '../api/dashboard';
import { Drawer, DrawerCloseButton } from '../components/ui/Drawer';
import { useToast } from '../components/toast/Toast';
import { timeOfDay } from '../lib/format';

interface Props {
  open: boolean;
  code: string | null;
  onClose: () => void;
}

export function AlertDrawer({ open, code, onClose }: Props) {
  const { show } = useToast();
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    enabled: !!code,
    queryKey: ['alert', code],
    queryFn: () => alertsApi.detail(code!),
  });

  const mutation = useMutation({
    mutationFn: (action: string) => alertsApi.action(code!, action),
    onSuccess: (_data, action) => {
      show(`${action} recorded for ${code} · Audit log updated`);
      void qc.invalidateQueries({ queryKey: ['alert', code] });
      void qc.invalidateQueries({ queryKey: ['alerts'] });
    },
    onError: () => show('Action failed — try again'),
  });

  const headerBg =
    data?.severity === 'critical' ? 'bg-red-600 text-white'   :
    data?.severity === 'warning'  ? 'bg-amber-500 text-white' :
                                    'bg-blue-500 text-white';

  return (
    <Drawer open={open && !!code} onClose={onClose}>
      <div className={`${data ? headerBg : 'bg-cream-200 text-navy-800'} px-6 py-5 flex items-start justify-between`}>
        <div>
          <div className="text-[10px] uppercase tracking-widest opacity-80">
            {data ? `${data.severity} alert · ${data.id}` : code ?? '—'}
          </div>
          <h2 className="serif-display text-xl font-medium mt-1 leading-snug">
            {data ? data.title : 'Loading…'}
          </h2>
          {data && (
            <div className="text-sm opacity-90 mt-2 font-numeric">
              {timeOfDay(data.triggered_at)}
              {data.district && ` · ${data.district}`}
              {' · Module '}{data.module}
            </div>
          )}
        </div>
        <DrawerCloseButton onClick={onClose} />
      </div>

      <div className="px-6 py-5 flex-1 overflow-y-auto space-y-5">
        {isLoading || !data ? (
          <div className="h-32 bg-cream-100 rounded-sm animate-pulse" />
        ) : (
          <>
            <div>
              <h3 className="text-xs uppercase tracking-widest text-navy-800/50 mb-2 font-semibold">Description</h3>
              <p className="text-sm text-navy-800 leading-relaxed">{data.description}</p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="bg-cream-50 p-3 rounded-sm">
                <div className="text-[10px] uppercase tracking-widest text-navy-800/50">Owner</div>
                <div className="text-sm font-medium text-navy-800 mt-1">{data.owner}</div>
              </div>
              <div className="bg-cream-50 p-3 rounded-sm">
                <div className="text-[10px] uppercase tracking-widest text-navy-800/50">Escalates to</div>
                <div className="text-sm font-medium text-navy-800 mt-1">{data.escalation_role}</div>
              </div>
              <div className="bg-cream-50 p-3 rounded-sm">
                <div className="text-[10px] uppercase tracking-widest text-navy-800/50">Status</div>
                <div className="text-sm font-medium text-navy-800 mt-1">{data.status}</div>
              </div>
              <div className="bg-cream-50 p-3 rounded-sm">
                <div className="text-[10px] uppercase tracking-widest text-navy-800/50">Source rule</div>
                <div className="text-xs text-navy-800 mt-1 font-numeric">{data.source_rule}</div>
              </div>
            </div>

            {data.actions.length > 0 && (
              <div>
                <h3 className="text-xs uppercase tracking-widest text-navy-800/50 mb-2 font-semibold">Action history</h3>
                <div className="space-y-2 text-xs">
                  {data.actions.map((a, i) => (
                    <div key={i} className="p-3 bg-cream-50 rounded-sm">
                      <div className="font-medium text-navy-800">{a.type}{a.role ? ` · ${a.role}` : ''}</div>
                      {a.notes && <div className="text-navy-800/70 mt-1">{a.notes}</div>}
                      <div className="text-navy-800/50 font-numeric mt-1">{timeOfDay(a.performed_at)} · {a.actor}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <div className="border-t border-cream-200 px-6 py-4 flex items-center gap-2 bg-cream-50">
        <button
          type="button"
          disabled={mutation.isPending || data?.status !== 'open'}
          onClick={() => mutation.mutate('acknowledge')}
          className="flex-1 bg-saffron-500 hover:bg-saffron-600 disabled:opacity-50 text-white text-sm py-2.5 rounded-sm transition"
        >
          {mutation.isPending ? 'Working…' : 'Acknowledge'}
        </button>
        <button
          type="button"
          disabled={mutation.isPending}
          onClick={() => mutation.mutate('escalate')}
          className="flex-1 bg-navy-800 hover:bg-navy-700 disabled:opacity-50 text-white text-sm py-2.5 rounded-sm transition"
        >
          Escalate
        </button>
        <button
          type="button"
          disabled={mutation.isPending}
          onClick={() => mutation.mutate('resolve')}
          className="px-4 border border-cream-300 text-navy-800/80 hover:bg-cream-100 text-sm py-2.5 rounded-sm"
        >
          Resolve
        </button>
      </div>
    </Drawer>
  );
}
